import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { Eye } from "lucide-react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  GenericMessagePreview,
  isProbablyHtml,
  stripHtml,
  WhatsAppBubblePreview,
} from "../../components/communication/MessagePreviewViews";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import {
  createCommunicationTemplate,
  getCommunicationTemplates,
  syncCommunicationTemplates,
} from "../../services/communicationTemplateApi";

/** Set true when POST /templates/sync is ready on the API */
const SYNC_TEMPLATES_ENABLED = false;

/** Matches backend `CHANNEL_VALUES` in Template model */
type ApiChannel = "whatsapp" | "sms" | "call" | "in-app" | "email";

type Channel = "WhatsApp" | "SMS" | "RCS" | "Email" | "In-App" | "Call";
type TemplateStatus = "Active" | "Inactive";

type Template = {
  templateCode: string;
  channel: Channel;
  name: string;
  description: string;
  serviceProvider: string;
  subject: string;
  body: string;
  isActive: boolean;
};

/** Auto template key: unique, human-readable, channel-prefixed (aligned with email-first flow). */
function generateTemplateCode(channel: ApiChannel): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  const stamp = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(
    d.getMinutes()
  )}${pad(d.getSeconds())}`;
  const prefix =
    channel === "email"
      ? "EMAIL"
      : channel === "whatsapp"
        ? "WA"
        : channel === "sms"
          ? "SMS"
          : channel === "call"
            ? "CALL"
            : "INAPP";
  return `${prefix}_${stamp}_${Math.random().toString(36).slice(2, 7)}`.toUpperCase();
}

const brand = "#EC6E51";

function extractBodyPlaceholderIndexes(body: string): number[] {
  const re = /\{\{(\d+)\}\}/g;
  const seen = new Set<number>();
  const out: number[] = [];
  let m: RegExpExecArray | null = null;
  while ((m = re.exec(String(body || ""))) !== null) {
    const n = Number(m[1]);
    if (!Number.isFinite(n) || n <= 0) continue;
    if (seen.has(n)) continue;
    seen.add(n);
    out.push(n);
  }
  return out.sort((a, b) => a - b);
}

function isEmailBodyEmpty(html: string): boolean {
  if (typeof document !== "undefined") {
    const doc = new DOMParser().parseFromString(html || "", "text/html");
    return (doc.body.textContent || "").trim().length === 0;
  }
  return !(html || "").replace(/<[^>]*>/g, "").trim();
}

const emailQuillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ indent: "-1" }, { indent: "+1" }],
    ["link"],
    ["clean"],
  ],
  clipboard: { matchVisual: false },
} as const;

const emailQuillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "list",
  "bullet",
  "indent",
  "link",
] as const;

const statusClass = (status: TemplateStatus) => {
  if (status === "Active") return "bg-emerald-100 text-emerald-700 border-emerald-200";
  return "bg-gray-100 text-gray-700 border-gray-200";
};

const statusFromActive = (isActive: boolean): TemplateStatus => (isActive ? "Active" : "Inactive");

const toChannel = (value: string): Channel => {
  const v = String(value || "").toLowerCase();
  if (v === "whatsapp") return "WhatsApp";
  if (v === "sms") return "SMS";
  if (v === "rcs") return "RCS";
  if (v === "email") return "Email";
  if (v === "call") return "Call";
  if (v === "in-app" || v === "inapp" || v === "in_app") return "In-App";
  return "In-App";
};

const toStatus = (value: string): TemplateStatus =>
  String(value || "").toLowerCase() === "inactive" ? "Inactive" : "Active";

/** Backend Template.status → table badge */
function statusFromApiDoc(item: Record<string, unknown>): TemplateStatus {
  const s = String(item?.status ?? "").toUpperCase();
  if (s === "ENABLED") return "Active";
  if (s === "DISABLED" || s === "PAUSED" || s === "REJECTED") return "Inactive";
  if (typeof item?.isActive === "boolean") return item.isActive ? "Active" : "Inactive";
  return toStatus(String(item?.status ?? "Active"));
}

function TemplateStatusWithPreview({ row }: { row: Template }) {
  const status = statusFromActive(row.isActive);
  const preview = useMemo(() => {
    const body = row.body?.trim() ?? "";
    if (!body) {
      return { fullHtml: null as string | null, fullPlain: "", hasBody: false };
    }
    const asHtml = isProbablyHtml(body);
    return {
      fullHtml: asHtml ? body : null,
      fullPlain: asHtml ? stripHtml(body) : body,
      hasBody: true,
    };
  }, [row.body]);

  const isWhatsApp = row.channel === "WhatsApp";

  return (
    <div className="flex min-w-0 items-center gap-2">
      <span
        className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${statusClass(status)}`}
      >
        {status}
      </span>
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="shrink-0 rounded p-1 text-gray-500 transition hover:bg-gray-100 hover:text-[#EC6E51]"
            aria-label="Show template body preview"
          >
            <Eye className="h-4 w-4" strokeWidth={2} />
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[min(100vw-2rem,20rem)] border-gray-200 p-0 shadow-lg data-[state=open]:z-[100]"
          align="end"
          side="bottom"
          sideOffset={6}
        >
          {!preview.hasBody ? (
            <div className="p-4 text-sm text-gray-600">No body content on this template.</div>
          ) : isWhatsApp ? (
            <WhatsAppBubblePreview
              fullHtml={preview.fullHtml}
              fullPlain={preview.fullPlain}
              sentAt={new Date().toISOString()}
            />
          ) : (
            <GenericMessagePreview fullHtml={preview.fullHtml} fullPlain={preview.fullPlain} />
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}

const CommunicationTemplates: React.FC = () => {
  const navigate = useNavigate();

  const channelOptions: Channel[] = ["WhatsApp", "SMS", "RCS", "Email", "In-App", "Call"];

  const createChannelOptions: { value: ApiChannel; label: string }[] = [
    { value: "email", label: "Email" },
    { value: "whatsapp", label: "WhatsApp" },
    { value: "sms", label: "SMS" },
    { value: "call", label: "Call" },
    { value: "in-app", label: "In-App" },
  ];

  const [templates, setTemplates] = useState<Template[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);

  const [selectedChannels, setSelectedChannels] = useState<Channel[]>([]);
  const [search, setSearch] = useState("");

  const [addOpen, setAddOpen] = useState(false);
  /** Create form aligned with `Template` schema (email-first). */
  const [addDraft, setAddDraft] = useState<{
    templateCode: string;
    channel: ApiChannel;
    name: string;
    category: "MARKETING" | "UTILITY" | "AUTHENTICATION";
    language: string;
    type: "TEXT" | "DOCUMENT" | "IMAGE" | "VIDEO";
    header: string;
    body: string;
    footer: string;
    params: string;
    status: "ENABLED" | "DISABLED" | "PAUSED" | "REJECTED";
  }>({
    templateCode: "",
    channel: "email",
    name: "",
    category: "UTILITY",
    language: "en",
    type: "TEXT",
    header: "",
    body: "",
    footer: "",
    params: "",
    status: "ENABLED",
  });
  const [addError, setAddError] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState<boolean>(false);
  const [isSyncing, setIsSyncing] = useState<boolean>(false);
  const detectedPlaceholderIndexes = useMemo(
    () => extractBodyPlaceholderIndexes(addDraft.body),
    [addDraft.body]
  );
  const parsedParams = useMemo(() => {
    if (!addDraft.params) return [];
    // Keep positional slots stable for placeholder-driven inputs.
    return addDraft.params.split("\n");
  }, [addDraft.params]);

  const getParamValueForSlot = (slotIdx: number): string =>
    parsedParams[slotIdx] ?? `var_${detectedPlaceholderIndexes[slotIdx] ?? slotIdx + 1}`;

  const setParamValueForSlot = (slotIdx: number, value: string) => {
    const next = [...parsedParams];
    while (next.length <= slotIdx) {
      const fallbackIdx = detectedPlaceholderIndexes[next.length] ?? next.length + 1;
      next.push(`var_${fallbackIdx}`);
    }
    next[slotIdx] = value;
    setAddDraft((prev) => ({ ...prev, params: next.join("\n") }));
  };

  const loadTemplates = async (pageArg: number = page, pageSizeArg: number = pageSize) => {
    try {
      setIsLoading(true);
      const channelFilter = selectedChannels.length ? selectedChannels.join(",") : undefined;

      const json = await getCommunicationTemplates({
        page: pageArg,
        pageSize: pageSizeArg,
        search: search.trim() || undefined,
        channels: channelFilter,
      });

      const rawItems = Array.isArray(json) ? json : Array.isArray((json as any)?.data) ? (json as any).data : [];

      const mapped: Template[] = rawItems.map((item: any, idx: number) => {
        const row = item as Record<string, unknown>;
        const st = statusFromApiDoc(row);
        return {
          templateCode: String(item?.templateCode || item?.templateKey || item?.key || item?._id || `TEMPLATE_${idx + 1}`),
          channel: toChannel(item?.channel),
          name: String(item?.name || item?.templateName || item?.templateCode || `Template ${idx + 1}`),
          description: String(item?.description || ""),
          serviceProvider: String(item?.serviceProvider || ""),
          subject: String(item?.subject || item?.header || ""),
          body: String(item?.body || item?.content || item?.template || ""),
          isActive: st === "Active",
        };
      });

      setTemplates(mapped);
      if (!Array.isArray(json) && json) {
        const anyJson: any = json;
        if (typeof anyJson.total === "number") {
          setTotal(anyJson.total);
        } else {
          setTotal(mapped.length);
        }
        if (typeof anyJson.page === "number") {
          setPage(anyJson.page);
        }
        if (typeof anyJson.pageSize === "number") {
          setPageSize(anyJson.pageSize);
        }
      } else {
        setTotal(mapped.length);
      }
    } catch (err: any) {
      setTemplates([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates(page, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, search, selectedChannels]);

  const filtered = useMemo(() => templates, [templates]);

  const toggleChannel = (c: Channel) => {
    setSelectedChannels((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));
  };

  const openAdd = () => {
    setAddError(null);
    const channel: ApiChannel = "email";
    setAddDraft({
      templateCode: generateTemplateCode(channel),
      channel,
      name: "",
      category: "UTILITY",
      language: "en",
      type: "TEXT",
      header: "",
      body: "",
      footer: "",
      params: "",
      status: "ENABLED",
    });
    setAddOpen(true);
  };

  const closeAdd = () => {
    setAddOpen(false);
    setAddError(null);
  };

  const saveAdd = async () => {
    const key = addDraft.templateCode.trim();
    if (!key) {
      setAddError("Template key is required.");
      return;
    }
    const name = addDraft.name.trim();
    if (!name) {
      setAddError("Template name is required.");
      return;
    }
    const bodyEmpty =
      addDraft.channel === "email"
        ? isEmailBodyEmpty(addDraft.body)
        : !addDraft.body.trim();
    if (bodyEmpty) {
      setAddError("Body is required.");
      return;
    }
    if (templates.some((t) => t.templateCode === key)) {
      setAddError("Template code already exists. Use a unique template code.");
      return;
    }
    const paramsList = addDraft.params
      .split(/[,;\n]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (detectedPlaceholderIndexes.length > 0) {
      if (paramsList.length < detectedPlaceholderIndexes.length) {
        setAddError(`Body uses ${detectedPlaceholderIndexes.length} placeholders. Fill all parameter fields.`);
        return;
      }
      const missing = paramsList.slice(0, detectedPlaceholderIndexes.length).some((p) => !p);
      if (missing) {
        setAddError("All placeholder parameters are required.");
        return;
      }
    }

    try {
      setIsAdding(true);
      setAddError(null);
      await createCommunicationTemplate({
        templateCode: key,
        channel: addDraft.channel,
        name,
        category: addDraft.category,
        language: addDraft.language.trim() || "en",
        type: addDraft.type,
        header: addDraft.header.trim() || null,
        body: addDraft.body,
        footer: addDraft.footer.trim() || null,
        params: paramsList,
        status: addDraft.status,
        buttonType: "NONE",
      });
      await loadTemplates();
      closeAdd();
    } catch (error: any) {
      setAddError(
        error?.response?.data?.error || error?.response?.data?.message || error?.message || "Failed to create template."
      );
    } finally {
      setIsAdding(false);
    }
  };

  const handleSyncTemplates = async () => {
    if (!SYNC_TEMPLATES_ENABLED) return;
    try {
      setIsSyncing(true);
      await syncCommunicationTemplates(
        selectedChannels.length ? { channels: selectedChannels.join(",") } : undefined
      );
      toast.success("Templates synced successfully.");
      await loadTemplates(1, pageSize);
      setPage(1);
    } catch {
      // Error toast from API helper
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Templates</h1>
          <p className="mt-1 text-gray-600">Manage communication templates (UI only)</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={openAdd}
            className="rounded-md border border-dashed px-4 py-2 text-sm font-semibold"
            style={{ borderColor: brand, color: brand }}
          >
            + Add Template
          </button>
          <button
            type="button"
            disabled={!SYNC_TEMPLATES_ENABLED || isSyncing}
            onClick={handleSyncTemplates}
            title={
              SYNC_TEMPLATES_ENABLED
                ? "Fetch latest templates from your provider"
                : "Template sync is not available yet (enable SYNC_TEMPLATES_ENABLED when the API is ready)."
            }
            className={
              SYNC_TEMPLATES_ENABLED
                ? "rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                : "cursor-not-allowed rounded-md border border-gray-200 bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-400"
            }
          >
            {isSyncing ? "Syncing…" : "Sync templates"}
          </button>
          <button
            onClick={() => navigate("/communication-server")}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            Back
          </button>
        </div>
      </div>

      <div className="mb-5 rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="w-full sm:w-[45%]">
            <label className="block text-sm font-medium text-gray-800">Search</label>
            <input
              value={search}
              onChange={(e) => {
                setPage(1);
                setSearch(e.target.value);
              }}
              placeholder="Search by key / name / content"
              className="mt-1 w-full rounded-md border border-gray-300 p-2 text-sm outline-none focus:border-[#EC6E51] focus:ring-1 focus:ring-[#EC6E51]"
            />
          </div>

          <div className="w-full sm:w-[55%]">
            <p className="mb-2 text-sm font-medium text-gray-800">Filter Channels</p>
            <div className="flex flex-wrap gap-3">
              {channelOptions.map((c) => (
                <label key={c} className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={selectedChannels.includes(c)}
                    onChange={() => {
                      setPage(1);
                      toggleChannel(c);
                    }}
                    className="h-4 w-4 rounded border-gray-300 accent-[#EC6E51]"
                  />
                  {c}
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      {isLoading && (
        <div className="mb-4 rounded-md border border-gray-200 bg-white px-4 py-3 text-sm text-gray-600">
          Loading templates from API...
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                Template Key
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                Channel
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                Name
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.map((t) => (
              <tr key={t.templateCode} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm text-gray-800">{t.templateCode}</td>
                <td className="px-4 py-3 text-sm text-gray-700">{t.channel}</td>
                <td className="px-4 py-3 text-sm text-gray-700">{t.name}</td>
                <td className="px-4 py-3 text-sm">
                  <TemplateStatusWithPreview row={t} />
                </td>
              </tr>
            ))}

            {filtered.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-10 text-center text-sm text-gray-500">
                  No templates found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-sm text-gray-700">
        <div>
          {total > 0 ? (
            <span>
              Showing{" "}
              <span className="font-semibold">
                {Math.min((page - 1) * pageSize + 1, total)}
              </span>{" "}
              to{" "}
              <span className="font-semibold">
                {Math.min(page * pageSize, total)}
              </span>{" "}
              of <span className="font-semibold">{total}</span> templates
            </span>
          ) : (
            <span>No templates to display</span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-1">
            <span className="text-xs text-gray-600">Rows per page</span>
            <select
              value={pageSize}
              onChange={(e) => {
                const nextSize = Number(e.target.value) || 10;
                setPage(1);
                setPageSize(nextSize);
              }}
              className="rounded-md border border-gray-300 px-2 py-1 text-xs outline-none focus:border-[#EC6E51] focus:ring-1 focus:ring-[#EC6E51]"
            >
              {[5, 10, 20, 50].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={page <= 1 || isLoading}
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              className="rounded-md border border-gray-300 px-2 py-1 text-xs font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-60 hover:bg-gray-50"
            >
              Prev
            </button>
            <span className="px-2 text-xs text-gray-600">
              Page <span className="font-semibold">{page}</span>
              {total > 0 && pageSize > 0 ? (
                <>
                  {" "}
                  of{" "}
                  <span className="font-semibold">
                    {Math.max(1, Math.ceil(total / pageSize))}
                  </span>
                </>
              ) : null}
            </span>
            <button
              type="button"
              disabled={pageSize <= 0 || page >= Math.max(1, Math.ceil(total / pageSize)) || isLoading}
              onClick={() =>
                setPage((prev) =>
                  pageSize > 0 ? Math.min(Math.max(1, Math.ceil(total / pageSize)), prev + 1) : prev + 1
                )
              }
              className="rounded-md border border-gray-300 px-2 py-1 text-xs font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-60 hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {addOpen && (
        <div className="fixed inset-0 z-[9999] flex min-h-0 items-center justify-center overflow-y-auto bg-black/50 p-4 py-6">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="add-template-title"
            className="my-auto flex w-full max-w-3xl max-h-[min(90dvh,calc(100vh-3rem))] flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg"
          >
            <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-100 px-4 pb-3 pt-4">
              <div className="min-w-0 pr-2">
                <h2 id="add-template-title" className="text-lg font-semibold text-gray-900">
                  Add Template
                </h2>
                <p className="text-sm text-gray-600">
                  Fields match the database template schema (email channel first). Template key is generated for you.
                </p>
              </div>
              <button
                type="button"
                onClick={closeAdd}
                className="shrink-0 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <div className="flex flex-wrap items-end gap-2">
                  <div className="min-w-0 flex-1">
                    <label className="mb-1 block text-sm font-medium text-gray-800">Template key</label>
                    <input
                      value={addDraft.templateCode}
                      onChange={(e) => setAddDraft({ ...addDraft, templateCode: e.target.value })}
                      placeholder="Unique code (stored as templateCode)"
                      className="w-full rounded-md border border-gray-300 p-2 font-mono text-sm outline-none focus:border-[#EC6E51] focus:ring-1 focus:ring-[#EC6E51]"
                    />
                    <p className="mt-1 text-[11px] text-gray-500">
                      Prefilled when you open this dialog. Use &quot;Regenerate&quot; for a new unique key.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setAddDraft((d) => ({ ...d, templateCode: generateTemplateCode(d.channel) }))
                    }
                    className="shrink-0 rounded-md border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    Regenerate
                  </button>
                </div>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-800">Channel</label>
                <select
                  value={addDraft.channel}
                  onChange={(e) => {
                    const ch = e.target.value as ApiChannel;
                    setAddDraft((d) => ({
                      ...d,
                      channel: ch,
                      templateCode: generateTemplateCode(ch),
                    }));
                  }}
                  className="w-full rounded-md border border-gray-300 p-2 text-sm outline-none focus:border-[#EC6E51] focus:ring-1 focus:ring-[#EC6E51]"
                >
                  {createChannelOptions.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-[11px] text-gray-500">
                  Email is the default. Changing channel regenerates the template key prefix.
                </p>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-800">Status</label>
                <select
                  value={addDraft.status}
                  onChange={(e) =>
                    setAddDraft({
                      ...addDraft,
                      status: e.target.value as (typeof addDraft)["status"],
                    })
                  }
                  className="w-full rounded-md border border-gray-300 p-2 text-sm outline-none focus:border-[#EC6E51] focus:ring-1 focus:ring-[#EC6E51]"
                >
                  <option value="ENABLED">ENABLED</option>
                  <option value="DISABLED">DISABLED</option>
                  <option value="PAUSED">PAUSED</option>
                  <option value="REJECTED">REJECTED</option>
                </select>
              </div>

              <div className="sm:col-span-2">
                <label className="mb-1 block text-sm font-medium text-gray-800">Name</label>
                <input
                  value={addDraft.name}
                  onChange={(e) => setAddDraft({ ...addDraft, name: e.target.value })}
                  placeholder="Display name (required)"
                  className="w-full rounded-md border border-gray-300 p-2 text-sm outline-none focus:border-[#EC6E51] focus:ring-1 focus:ring-[#EC6E51]"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-800">Category</label>
                <select
                  value={addDraft.category}
                  onChange={(e) =>
                    setAddDraft({
                      ...addDraft,
                      category: e.target.value as (typeof addDraft)["category"],
                    })
                  }
                  className="w-full rounded-md border border-gray-300 p-2 text-sm outline-none focus:border-[#EC6E51] focus:ring-1 focus:ring-[#EC6E51]"
                >
                  <option value="MARKETING">MARKETING</option>
                  <option value="UTILITY">UTILITY</option>
                  <option value="AUTHENTICATION">AUTHENTICATION</option>
                </select>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-800">Language</label>
                <input
                  value={addDraft.language}
                  onChange={(e) => setAddDraft({ ...addDraft, language: e.target.value })}
                  placeholder="en"
                  className="w-full rounded-md border border-gray-300 p-2 text-sm outline-none focus:border-[#EC6E51] focus:ring-1 focus:ring-[#EC6E51]"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-800">Message type</label>
                <select
                  value={addDraft.type}
                  onChange={(e) =>
                    setAddDraft({
                      ...addDraft,
                      type: e.target.value as (typeof addDraft)["type"],
                    })
                  }
                  className="w-full rounded-md border border-gray-300 p-2 text-sm outline-none focus:border-[#EC6E51] focus:ring-1 focus:ring-[#EC6E51]"
                >
                  <option value="TEXT">TEXT</option>
                  <option value="DOCUMENT">DOCUMENT</option>
                  <option value="IMAGE">IMAGE</option>
                  <option value="VIDEO">VIDEO</option>
                </select>
              </div>

              <div
                className={
                  addDraft.channel === "email"
                    ? "sm:col-span-2 rounded-md border border-sky-100 bg-sky-50/80 p-3"
                    : "sm:col-span-2"
                }
              >
                {addDraft.channel === "email" ? (
                  <p className="mb-2 text-xs font-semibold text-sky-900">Email — subject line</p>
                ) : null}
                <label className="mb-1 block text-sm font-medium text-gray-800">
                  {addDraft.channel === "email" ? (
                    <>
                      Subject <span className="font-normal text-gray-500">(stored as `header`)</span>
                    </>
                  ) : (
                    <>
                      Header <span className="font-normal text-gray-500">(optional, `header`)</span>
                    </>
                  )}
                </label>
                <input
                  value={addDraft.header}
                  onChange={(e) => setAddDraft({ ...addDraft, header: e.target.value })}
                  placeholder={
                    addDraft.channel === "email"
                      ? "e.g. Your weekly update from Acme"
                      : "Optional header line"
                  }
                  className="w-full rounded-md border border-gray-300 bg-white p-2 text-sm outline-none focus:border-[#EC6E51] focus:ring-1 focus:ring-[#EC6E51]"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="mb-1 block text-sm font-medium text-gray-800">
                  Body <span className="text-rose-600">*</span>
                </label>
                {addDraft.channel === "email" ? (
                  <>
                    <p className="mb-2 text-[11px] text-gray-500">
                      Rich HTML for email. You can type placeholders like{" "}
                      <code className="rounded bg-gray-100 px-1">{`{{1}}`}</code>,{" "}
                      <code className="rounded bg-gray-100 px-1">{`{{2}}`}</code> in the editor.
                    </p>
                    <div
                      className="rounded-md border border-gray-300 bg-white [&_.ql-container]:min-h-[160px] [&_.ql-editor]:min-h-[140px] [&_.ql-toolbar]:rounded-t-md [&_.ql-container]:rounded-b-md [&_.ql-picker-options]:z-[10050]"
                    >
                      <ReactQuill
                        theme="snow"
                        value={addDraft.body}
                        onChange={(value) => setAddDraft({ ...addDraft, body: value })}
                        placeholder="Compose email body…"
                        className="communication-template-email-quill"
                        modules={emailQuillModules}
                        formats={[...emailQuillFormats]}
                      />
                    </div>
                  </>
                ) : (
                  <textarea
                    rows={6}
                    value={addDraft.body}
                    onChange={(e) => setAddDraft({ ...addDraft, body: e.target.value })}
                    placeholder="HTML or plain text. Use {{1}}, {{2}} for positional params if needed."
                    className="w-full rounded-md border border-gray-300 p-2 font-mono text-sm outline-none focus:border-[#EC6E51] focus:ring-1 focus:ring-[#EC6E51]"
                  />
                )}
              </div>

              <div className="sm:col-span-2">
                <label className="mb-1 block text-sm font-medium text-gray-800">
                  Footer <span className="font-normal text-gray-500">(optional, DB `footer`)</span>
                </label>
                <input
                  value={addDraft.footer}
                  onChange={(e) => setAddDraft({ ...addDraft, footer: e.target.value })}
                  className="w-full rounded-md border border-gray-300 p-2 text-sm outline-none focus:border-[#EC6E51] focus:ring-1 focus:ring-[#EC6E51]"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="mb-1 block text-sm font-medium text-gray-800">
                  Params <span className="font-normal text-gray-500">(stored in `params[]`)</span>
                </label>
                {detectedPlaceholderIndexes.length > 0 ? (
                  <div className="space-y-2 rounded-md border border-gray-200 bg-gray-50 p-3">
                    <p className="text-xs text-gray-600">
                      Body has <span className="font-semibold">{detectedPlaceholderIndexes.length}</span> placeholders.
                      Provide one param name for each placeholder.
                    </p>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      {detectedPlaceholderIndexes.map((placeholderNo, slotIdx) => (
                        <div key={`param-slot-${placeholderNo}`}>
                          <label className="mb-1 block text-xs font-medium text-gray-700">{`{{${placeholderNo}}} param`}</label>
                          <input
                            value={getParamValueForSlot(slotIdx)}
                            onChange={(e) => setParamValueForSlot(slotIdx, e.target.value.trim())}
                            placeholder={`var_${placeholderNo}`}
                            className="w-full rounded-md border border-gray-300 bg-white p-2 text-sm outline-none focus:border-[#EC6E51] focus:ring-1 focus:ring-[#EC6E51]"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <textarea
                    rows={2}
                    value={addDraft.params}
                    onChange={(e) => setAddDraft({ ...addDraft, params: e.target.value })}
                    placeholder="e.g. name, email, company"
                    className="w-full rounded-md border border-gray-300 p-2 text-sm outline-none focus:border-[#EC6E51] focus:ring-1 focus:ring-[#EC6E51]"
                  />
                )}
              </div>
            </div>

            {addError && (
              <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
                {addError}
              </div>
            )}
            </div>

            <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-gray-100 bg-white px-4 py-3">
              <button
                type="button"
                onClick={closeAdd}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveAdd}
                disabled={isAdding}
                className="rounded-md bg-[#EC6E51] px-4 py-2 text-sm font-semibold text-white hover:bg-[#d85f44] disabled:opacity-60"
              >
                {isAdding ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommunicationTemplates;

